import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, Pencil, Plus, Star, Trash2, X } from "lucide-react";
import { AdminShell } from "../components/admin/AdminShell";
import { API, fmtPrice } from "../lib/eventUtils";

const EMPTY = {
  name: "", slug: "", description: "", price: "", compare_at: "", inventory: "0",
  low_stock_threshold: "5", sizes: "", colors: "", category: "", media_url: "", status: "draft", featured: false,
};

const toCents = (v) => Math.round((parseFloat(v) || 0) * 100);
const list = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-400 block mb-1">{label}</span>
    {children}
  </label>
);

export default function AdminMerch() {
  const [products, setProducts] = useState(null);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/products`, { withCredentials: true });
      setProducts(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not load products");
    }
  };
  useEffect(() => { load(); }, []);

  const openForm = (p) => {
    setEditing(p || "new");
    setF(p ? {
      name: p.name, slug: p.slug, description: p.description || "",
      price: p.price_cents ? (p.price_cents / 100).toString() : "",
      compare_at: p.compare_at_cents ? (p.compare_at_cents / 100).toString() : "",
      inventory: String(p.inventory ?? 0), low_stock_threshold: String(p.low_stock_threshold ?? 5),
      sizes: (p.sizes || []).join(", "), colors: (p.colors || []).join(", "),
      category: p.category || "", media_url: p.media_url || "", status: p.status, featured: !!p.featured,
    } : EMPTY);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      name: f.name, description: f.description, category: f.category,
      price_cents: toCents(f.price),
      compare_at_cents: f.compare_at ? toCents(f.compare_at) : null,
      inventory: Number(f.inventory) || 0,
      low_stock_threshold: Number(f.low_stock_threshold) || 0,
      sizes: list(f.sizes), colors: list(f.colors),
      media_url: f.media_url || null, status: f.status, featured: f.featured,
    };
    if (f.slug) payload.slug = f.slug;
    try {
      if (editing === "new") {
        await axios.post(`${API}/api/admin/products`, payload, { withCredentials: true });
        toast.success("Product created");
      } else {
        await axios.patch(`${API}/api/admin/products/${editing.product_id}`, payload, { withCredentials: true });
        toast.success("Product updated");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const patch = async (p, data, msg) => {
    try {
      await axios.patch(`${API}/api/admin/products/${p.product_id}`, data, { withCredentials: true });
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/api/admin/products/${p.product_id}`, { withCredentials: true });
      toast.success("Product deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <AdminShell title="Merch">
      <div data-testid="admin-merch-page">
        <div className="flex justify-end mb-6">
          <button onClick={() => openForm(null)} className="btn-volt !py-2.5 !px-5 !text-sm" data-testid="merch-new-button">
            <Plus size={15} /> New Product
          </button>
        </div>

        {editing && (
          <form onSubmit={submit} className="border border-volt/40 bg-[#0c0c0c] p-6 sm:p-8 mb-8" data-testid="merch-form">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-head font-extrabold uppercase text-2xl tracking-tight">{editing === "new" ? "New Product" : `Edit: ${editing.name}`}</h2>
              <button type="button" onClick={() => setEditing(null)} className="text-zinc-500 hover:text-white" data-testid="merch-form-close" aria-label="Close form"><X size={20} /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <Field label="Name *"><input required value={f.name} onChange={set("name")} className="volt-input" data-testid="merch-form-name" /></Field>
              <Field label="Slug"><input value={f.slug} onChange={set("slug")} placeholder="auto-generated" className="volt-input" data-testid="merch-form-slug" /></Field>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
              <Field label="Price ($)"><input type="number" min="0" step="0.01" value={f.price} onChange={set("price")} className="volt-input" data-testid="merch-form-price" /></Field>
              <Field label="Compare-at ($)"><input type="number" min="0" step="0.01" value={f.compare_at} onChange={set("compare_at")} className="volt-input" data-testid="merch-form-compare" /></Field>
              <Field label="Inventory"><input type="number" min="0" value={f.inventory} onChange={set("inventory")} className="volt-input" data-testid="merch-form-inventory" /></Field>
              <Field label="Low-stock at"><input type="number" min="0" value={f.low_stock_threshold} onChange={set("low_stock_threshold")} className="volt-input" data-testid="merch-form-lowstock" /></Field>
            </div>
            <div className="grid sm:grid-cols-3 gap-4 mb-4">
              <Field label="Category"><input value={f.category} onChange={set("category")} placeholder="Tee, Hoodie…" className="volt-input" data-testid="merch-form-category" /></Field>
              <Field label="Sizes (comma separated)"><input value={f.sizes} onChange={set("sizes")} placeholder="S, M, L, XL" className="volt-input" data-testid="merch-form-sizes" /></Field>
              <Field label="Colors (comma separated)"><input value={f.colors} onChange={set("colors")} placeholder="Black, Volt" className="volt-input" data-testid="merch-form-colors" /></Field>
            </div>
            <div className="mb-4">
              <Field label="Approved DPS media URL"><input value={f.media_url} onChange={set("media_url")} placeholder="/dps/… or https://…" className="volt-input" data-testid="merch-form-media" /></Field>
            </div>
            <div className="mb-4">
              <Field label="Description"><textarea rows={3} value={f.description} onChange={set("description")} className="volt-input" data-testid="merch-form-description" /></Field>
            </div>
            <div className="flex flex-wrap items-center gap-6 mb-6">
              <Field label="Status">
                <select value={f.status} onChange={set("status")} className="volt-input !w-auto" data-testid="merch-form-status">
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                </select>
              </Field>
              <label className="flex items-center gap-2 mt-5 cursor-pointer">
                <input type="checkbox" checked={f.featured} onChange={set("featured")} data-testid="merch-form-featured" />
                <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-400">Featured</span>
              </label>
            </div>
            <div className="flex gap-3">
              <button type="submit" disabled={busy} className="btn-volt disabled:opacity-60" data-testid="merch-form-save">{busy ? "Saving…" : "Save Product"}</button>
              <button type="button" onClick={() => setEditing(null)} className="btn-ghost" data-testid="merch-form-cancel">Cancel</button>
            </div>
          </form>
        )}

        {error && <p className="border border-red-500/40 bg-red-500/10 text-red-300 text-sm px-4 py-3 mb-6" data-testid="merch-error">{error}</p>}
        {products === null && !error && <p className="font-mono2 text-xs uppercase tracking-[0.25em] text-zinc-500 animate-pulse" data-testid="merch-loading">Loading…</p>}
        {products !== null && products.length === 0 && (
          <p className="text-zinc-500 text-sm border border-white/10 bg-[#0c0c0c] p-8" data-testid="merch-empty">
            No products yet. Create your first real product — no fake placeholders.
          </p>
        )}

        {products !== null && products.length > 0 && (
          <div className="overflow-x-auto border border-white/10 bg-[#0c0c0c]">
            <table className="w-full text-sm" data-testid="merch-table">
              <thead>
                <tr className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-white/10 text-left">
                  <th className="py-2.5 px-4">Product</th>
                  <th className="py-2.5 pr-4">Price</th>
                  <th className="py-2.5 pr-4">Inventory</th>
                  <th className="py-2.5 pr-4">Status</th>
                  <th className="py-2.5 pr-4">Stripe</th>
                  <th className="py-2.5 pr-4"></th>
                </tr>
              </thead>
              <tbody>
                {products.map((p, i) => {
                  const low = p.status === "active" && (p.inventory ?? 0) <= (p.low_stock_threshold ?? 5);
                  return (
                    <tr key={p.product_id} className="border-b border-white/5 text-zinc-300" data-testid={`merch-row-${i}`}>
                      <td className="py-2.5 px-4">
                        <p className="font-semibold text-white flex items-center gap-2">
                          {p.featured && <Star size={13} className="text-volt" fill="currentColor" />}{p.name}
                        </p>
                        <p className="text-zinc-600 text-xs">{p.category || "—"}{p.sizes?.length ? ` · ${p.sizes.join("/")}` : ""}</p>
                      </td>
                      <td className="py-2.5 pr-4 font-mono2 text-xs">
                        <span className="text-white">{fmtPrice(p.price_cents || 0)}</span>
                        {p.compare_at_cents ? <span className="text-zinc-600 line-through ml-2">{fmtPrice(p.compare_at_cents)}</span> : null}
                      </td>
                      <td className="py-2.5 pr-4">
                        <span className={low ? "text-amber-400 flex items-center gap-1.5" : ""} data-testid={`merch-inventory-${i}`}>
                          {low && <AlertTriangle size={13} />}{p.inventory ?? 0}{low ? " · low" : ""}
                        </span>
                      </td>
                      <td className="py-2.5 pr-4">
                        <span className={`font-mono2 text-[9px] uppercase tracking-[0.15em] border px-2 py-0.5 ${p.status === "active" ? "border-volt/60 text-volt" : "border-white/20 text-zinc-500"}`}>
                          {p.status}
                        </span>
                      </td>
                      <td className="py-2.5 pr-4 font-mono2 text-[10px] text-zinc-600">{p.stripe_price_id ? "synced" : "—"}</td>
                      <td className="py-2.5 pr-4">
                        <div className="flex justify-end gap-2">
                          <button onClick={() => openForm(p)} className="btn-ghost !py-1.5 !px-3 !text-xs" data-testid={`merch-edit-${i}`}><Pencil size={12} /> Edit</button>
                          <button onClick={() => patch(p, { status: p.status === "active" ? "draft" : "active" }, p.status === "active" ? "Moved to draft" : "Product activated")} className="btn-ghost !py-1.5 !px-3 !text-xs" data-testid={`merch-toggle-${i}`}>
                            {p.status === "active" ? "Draft" : "Activate"}
                          </button>
                          <button onClick={() => remove(p)} className="text-zinc-600 hover:text-red-400 transition-colors px-1" data-testid={`merch-delete-${i}`} title="Delete product">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminShell>
  );
}
